import type { NoticeWithType } from "../../components/ViewNotices";
import { noticeHref } from "../../routes";

/** What another page hands a Notice's page in router state: whether to open Send reminder ready. */
export type NoticeHandover = { openReminder?: boolean };

const REMINDER_PARAM = "reminder";

/**
 * A Notice page's address that opens Send reminder ready, for links opened in
 * a new tab (as Nova's are), where router state doesn't reach. Nothing is sent
 * until the Team member presses Send reminder.
 */
export const noticeReminderHref = (noticeId: string) => `${noticeHref(noticeId)}?${REMINDER_PARAM}=1`;

/**
 * A Notice page's handover, read on arrival from its router state or its
 * address; anything else is no handover.
 */
export const readNoticeHandover = (state: unknown, search = ""): NoticeHandover => {
  const { openReminder } = (state ?? {}) as NoticeHandover;
  const fromAddress = new URLSearchParams(search).get(REMINDER_PARAM) === "1";
  return openReminder === true || fromAddress ? { openReminder: true } : {};
};

/** The address's query without the handover, so a reload doesn't open Send reminder again. */
export const searchWithoutHandover = (search: string): string => {
  const params = new URLSearchParams(search);
  params.delete(REMINDER_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
};

export const toNotice = (notice: NoticeWithType, handover: NoticeHandover = {}) => ({
  pathname: noticeHref(notice.noticeId),
  state: { notice, ...handover },
});

/**
 * The Notice a Notice page shows: the fresh copy once Notices are loaded,
 * otherwise the one handed over. Opened from just its address (a new tab,
 * as Nova's "Open original" does), it waits for Notices to load.
 */
export function noticeToShow<T extends { noticeId?: string }>(
  handedOver: T | undefined,
  idFromAddress: string | undefined,
  loaded: T[] | null | undefined,
): T | undefined | "loading" {
  const noticeId = handedOver?.noticeId ?? idFromAddress;
  if (!noticeId) return handedOver;
  if (!loaded) return handedOver ?? "loading";
  return loaded.find((notice) => notice.noticeId === noticeId) ?? handedOver;
}
